import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

const Section = ({ number, title, children }) => (
  <motion.div variants={fadeInUp} className="mb-10 sm:mb-14" data-testid={`privacy-section-${number}`}>
    <div className="flex items-baseline gap-3 sm:gap-4 mb-4 sm:mb-5">
      <span className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl text-[#D4AF37] font-semibold leading-none">
        {number}.
      </span>
      <h2 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl md:text-3xl text-white">
        {title}
      </h2>
    </div>
    <div className="pl-0 sm:pl-10 text-zinc-400 text-sm sm:text-base leading-relaxed space-y-4">
      {children}
    </div>
  </motion.div>
);

const SubSection = ({ number, title, children }) => (
  <div className="mb-6 sm:mb-8">
    <h3 className="font-['Cormorant_Garamond'] text-lg sm:text-xl text-white/90 mb-3 flex items-baseline gap-2">
      <span className="text-[#D4AF37]/70 text-base">{number}</span>
      {title}
    </h3>
    <div className="text-zinc-400 text-sm sm:text-base leading-relaxed space-y-3 pl-0 sm:pl-6">
      {children}
    </div>
  </div>
);

export default function PrivacyPolicyPage() {
  return (
    <div data-testid="privacy-policy-page">
      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-10 sm:pb-16 bg-[#030303] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-10 left-1/3 w-72 sm:w-[500px] h-72 sm:h-[500px] bg-[#D4AF37] rounded-full filter blur-[120px] sm:blur-[200px]" />
        </div>

        <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-4xl relative z-10">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.div variants={fadeInUp}>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-zinc-500 hover:text-[#D4AF37] transition-colors text-xs sm:text-sm mb-8 sm:mb-12"
                data-testid="privacy-back-link"
              >
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                Vissza a főoldalra
              </Link>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5">
              <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37]" strokeWidth={1.5} />
              <p className="text-[0.6rem] sm:text-[0.65rem] text-[#D4AF37] tracking-[0.2em] sm:tracking-[0.3em] uppercase">
                Jogi dokumentum
              </p>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-white mb-4 sm:mb-5 leading-tight"
            >
              Adatvédelmi Tájékoztató
            </motion.h1>

            <motion.div variants={fadeInUp} className="gold-line mb-5 sm:mb-6" />

            <motion.p variants={fadeInUp} className="text-zinc-500 text-xs sm:text-sm">
              Hatályos: 2026. 04. 09. – visszavonásig, vagy módosításig
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-10 sm:py-16 bg-[#050505]">
        <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-4xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.05 }}
            variants={staggerContainer}
          >
            {/* 1. Adatkezelő adatai */}
            <Section number="1" title="Adatkezelő adatai">
              <div className="premium-card p-5 sm:p-8 space-y-2 sm:space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                  <span className="text-zinc-500 text-xs sm:text-sm min-w-[140px]">Megnevezés:</span>
                  <span className="text-white text-sm sm:text-base">Prikler Márk</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                  <span className="text-zinc-500 text-xs sm:text-sm min-w-[140px]">Székhely:</span>
                  <span className="text-white text-sm sm:text-base">9200 Mosonmagyaróvár, Április utca 31.</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                  <span className="text-zinc-500 text-xs sm:text-sm min-w-[140px]">Adószám:</span>
                  <span className="text-white text-sm sm:text-base">91004288-1-28</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                  <span className="text-zinc-500 text-xs sm:text-sm min-w-[140px]">Telefon:</span>
                  <span className="text-white text-sm sm:text-base">+36 30 566 4154</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                  <span className="text-zinc-500 text-xs sm:text-sm min-w-[140px]">Email:</span>
                  <span className="text-white text-sm sm:text-base">priklermark05@gmail.com</span>
                </div>
              </div>
            </Section>

            {/* 2. Adatvédelmi nyilatkozat */}
            <Section number="2" title="Adatvédelmi nyilatkozat">
              <p>
                Ez az Adatvédelmi tájékoztató és nyilatkozat (a továbbiakban: Tájékoztató) a Prikler Márk E.V. (továbbiakban: Adatkezelő) tevékenységére vonatkozóan tartalmazza az adatvédelemmel kapcsolatos információkat.
              </p>
              <p>
                Szolgáltatásunk igénybevétele során Ön megadja személyes adatait. Ezeket az adatokat a legnagyobb körültekintéssel, a jogszabályok előírásainak megfelelően kezeljük, és minden esetben igyekszünk az Ön adatkezeléssel kapcsolatos igényeinek és elvárásainak megfelelni.
              </p>
              <p>
                Az adatok kezelése során mindig különös gondossággal járunk el, és az adatokat védjük az illetéktelen hozzáféréstől. Ezt kiemelt fontosságúnak tekintjük.
              </p>
              <p className="text-zinc-500 text-xs sm:text-sm">Az adatkezelési tevékenységünket meghatározó jogszabályok:</p>
              <ul className="list-none space-y-2 text-xs sm:text-sm text-zinc-400">
                <li className="flex gap-2 items-start">
                  <span className="text-[#D4AF37] mt-1.5 text-[6px]">&#9679;</span>
                  Az Európai Parlament és a Tanács (EU) 2016/679 rendelete – GDPR
                </li>
                <li className="flex gap-2 items-start">
                  <span className="text-[#D4AF37] mt-1.5 text-[6px]">&#9679;</span>
                  2011. évi CXII. törvény (Infotv.)
                </li>
                <li className="flex gap-2 items-start">
                  <span className="text-[#D4AF37] mt-1.5 text-[6px]">&#9679;</span>
                  Magyarország Alaptörvénye
                </li>
                <li className="flex gap-2 items-start">
                  <span className="text-[#D4AF37] mt-1.5 text-[6px]">&#9679;</span>
                  2013. évi V. törvény (Ptk.)
                </li>
                <li className="flex gap-2 items-start">
                  <span className="text-[#D4AF37] mt-1.5 text-[6px]">&#9679;</span>
                  2003. évi C. törvény az elektronikus hírközlésről (Eht.)
                </li>
                <li className="flex gap-2 items-start">
                  <span className="text-[#D4AF37] mt-1.5 text-[6px]">&#9679;</span>
                  2001. évi CVIII. törvény (Eker tv.)
                </li>
                <li className="flex gap-2 items-start">
                  <span className="text-[#D4AF37] mt-1.5 text-[6px]">&#9679;</span>
                  2017. évi CL. törvény (Art.)
                </li>
                <li className="flex gap-2 items-start">
                  <span className="text-[#D4AF37] mt-1.5 text-[6px]">&#9679;</span>
                  2000. évi C. törvény (Sztv.)
                </li>
                <li className="flex gap-2 items-start">
                  <span className="text-[#D4AF37] mt-1.5 text-[6px]">&#9679;</span>
                  1997. évi CLV. törvény (Fogyv tv.)
                </li>
                <li className="flex gap-2 items-start">
                  <span className="text-[#D4AF37] mt-1.5 text-[6px]">&#9679;</span>
                  2008. évi XLVIII. törvény (Grtv.)
                </li>
              </ul>
            </Section>

            {/* 3. Fogalmak */}
            <Section number="3" title="Fogalmak">
              <div className="space-y-4">
                {[
                  { term: "Személyes adat", def: "Az érintettre vonatkozó bármely információ, például valamely azonosító, név, szám, helymeghatározó adat, online azonosító vagy a természetes személy testi, fiziológiai, genetikai, szellemi, gazdasági, kulturális vagy szociális azonosságára vonatkozó adat." },
                  { term: "Különleges adatok", def: "A faji vagy etnikai származásra, politikai véleményre, vallási vagy világnézeti meggyőződésre vagy szakszervezeti tagságra utaló személyes adatok, valamint a természetes személyek egyedi azonosítását célzó genetikai és biometrikus adatok, az egészségügyi adatok és a természetes személyek szexuális életére vagy szexuális irányultságára vonatkozó személyes adatok." },
                  { term: "Adatkezelés", def: "Az alkalmazott eljárástól függetlenül a személyes adatokon vagy adatállományokon végzett bármely művelet vagy a műveletek összessége, így különösen gyűjtése, felvétele, rögzítése, rendszerezése, tagolása, tárolása, megváltoztatása, átalakítása, felhasználása, lekérdezése, továbbítása, nyilvánosságra hozatala, összehangolása vagy összekapcsolása, zárolása, törlése és megsemmisítése." },
                  { term: "Adatkezelő", def: "Az a természetes vagy jogi személy, illetve jogi személyiséggel nem rendelkező szervezet, aki vagy amely önállóan vagy másokkal együtt a személyes adatok kezelésének célját és eszközeit meghatározza." },
                  { term: "Adatfeldolgozó", def: "Az a természetes vagy jogi személy, illetve jogi személyiséggel nem rendelkező szervezet, aki, vagy amely az adatkezelő nevében személyes adatokat kezel." },
                  { term: "Érintett", def: "Bármely meghatározott személyes adat alapján azonosított vagy – közvetlenül vagy közvetve – azonosítható természetes személy." },
                  { term: "Adattovábbítás", def: "A személyes adat meghatározott harmadik személy számára történő hozzáférhetővé tétele." },
                  { term: "Adattörlés", def: "Az adatok felismerhetetlenné tétele tartalomtörléssel vagy ezzel egyenértékű eredményt lehetővé tevő módon." },
                  { term: "Adatvédelmi incidens", def: "A biztonság olyan sérülése, amely a továbbított, tárolt vagy más módon kezelt személyes adatok véletlen vagy jogellenes megsemmisítését, elvesztését, megváltoztatását, jogosulatlan közlését vagy az azokhoz való jogosulatlan hozzáférést eredményezi." },
                ].map((item, idx) => (
                  <div key={idx} className="border-l border-white/5 pl-4 sm:pl-5">
                    <p className="text-white text-sm sm:text-base font-medium mb-1">{item.term}</p>
                    <p className="text-xs sm:text-sm">{item.def}</p>
                  </div>
                ))}
              </div>
            </Section>

            {/* 4. Az adatkezelés alapvető elvei */}
            <Section number="4" title="Az adatkezelés alapvető elvei">
              <p>A személyes adatok:</p>
              <ul className="list-none space-y-3 text-xs sm:text-sm">
                {[
                  { label: "Jogszerűség", text: "Kezelését jogszerűen és tisztességesen, valamint az érintett számára átlátható módon végezzük." },
                  { label: "Célhoz kötöttség", text: "Gyűjtése csak meghatározott, egyértelmű és jogszerű célból történhet." },
                  { label: "Adattakarékosság", text: "Az adatkezelés céljai szempontjából megfelelőek és relevánsak kell, hogy legyenek, és a szükségesre kell korlátozódniuk." },
                  { label: "Pontosság", text: "Minden észszerű intézkedést megteszünk annak érdekében, hogy az adatok pontosak és naprakészek legyenek." },
                  { label: "Korlátozott tárolhatóság", text: "Tárolását olyan formában végezzük, amely az érintettek azonosítását csak a szükséges ideig teszi lehetővé." },
                  { label: "Integritás és bizalmas jelleg", text: "Kezelését oly módon végezzük, hogy megfelelő technikai vagy szervezési intézkedések alkalmazásával biztosítva legyen a személyes adatok megfelelő biztonsága." },
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-3 items-start">
                    <span className="text-[#D4AF37] font-medium min-w-fit text-xs sm:text-sm">{item.label}:</span>
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </Section>

            {/* 5. Kezelt adatok */}
            <Section number="5" title="Kezelt adatok">
              <SubSection number="5.1" title="Szolgáltatás megrendelése és időpontfoglalás">
                <p>
                  <strong className="text-white/80">Érintettek:</strong> Azok a természetes/jogi személyek, akik a szolgáltatást megrendelik és időpontot foglalnak.
                </p>
                <p>
                  <strong className="text-white/80">Adatkezelés célja:</strong> Kapcsolattartás, a megrendelés teljesítése.
                </p>

                {/* Data Table */}
                <div className="overflow-x-auto mt-4">
                  <table className="w-full text-xs sm:text-sm">
                    <thead>
                      <tr className="border-b border-[#D4AF37]/20">
                        <th className="text-left py-3 pr-4 text-[#D4AF37] font-medium">Adatfajta</th>
                        <th className="text-left py-3 pr-4 text-[#D4AF37] font-medium">Jogalap</th>
                        <th className="text-left py-3 text-[#D4AF37] font-medium">Őrzési idő</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-400">
                      {["Név", "E-mail cím", "Telefonszám", "Lakcím", "Időpont"].map((field, idx) => (
                        <tr key={idx} className="border-b border-white/5">
                          <td className="py-2.5 pr-4 text-white/70">{field}</td>
                          <td className="py-2.5 pr-4">{idx === 0 ? "GDPR 6. cikk a) pont (hozzájárulás)" : ""}</td>
                          <td className="py-2.5">{idx === 0 ? "Hozzájárulás visszavonásáig" : ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="mt-4">
                  A fenti adatok megadása nem kötelező, ezek hiányában azonban nem tudunk kapcsolatot tartani Önnel és nem tudjuk a megrendelését teljesíteni. A hozzájárulását bármikor indokolás nélkül visszavonhatja, ez azonban nem érinti a korábban, a hozzájárulása alapján történt adatkezeléseket.
                </p>
              </SubSection>
            </Section>

            {/* 6. Az adatok biztonsága */}
            <Section number="6" title="Az adatok biztonsága">
              <p>
                Technikai és szervezési intézkedésekkel, valamint eljárások kialakításával gondoskodunk az általunk kezelt személyes adatok biztonságáról.
              </p>
              <p>
                Az adatokat megfelelő intézkedésekkel védjük a jogosulatlan hozzáférés, megváltoztatás, továbbítás, nyilvánosságra hozatal, törlés vagy megsemmisítés, valamint a véletlen megsemmisülés és sérülés ellen.
              </p>
              <p>
                A személyes adatokhoz csak azok a munkatársaink férnek hozzá, akiknek feladataik ellátásához szükségük van azok megismerésére.
              </p>
            </Section>

            {/* 7. Az adatok továbbítása, átadása */}
            <Section number="7" title="Az adatok továbbítása, átadása">
              <p>
                A szolgáltatásunkat igénybe vevő természetes személyek személyes adatait kizárólag az ebben a pontban meghatározott partnereinknek, adatfeldolgozóinknak továbbítjuk, illetve adjuk át, valamint a hatóságoknak megkeresés esetén.
              </p>
              <div className="premium-card p-4 sm:p-6 mt-4">
                <p className="text-zinc-500 text-xs uppercase tracking-widest mb-3">Szerződéses partner</p>
                <p className="text-white text-sm sm:text-base">HighLevel Inc. (tárhelyszolgáltató)</p>
                <p className="text-zinc-500 text-xs sm:text-sm mt-1">400 North Saint Paul Street Suite 920, Dallas, TX 75201</p>
              </div>
            </Section>

            {/* 8. Az érintettek jogai */}
            <Section number="8" title="Az érintettek jogai">
              <SubSection number="8.1" title="Előzetes tájékoztatáshoz való jog">
                <p>Az érintett jogosult arra, hogy a személyes adatokra vonatkozó adatkezelés megkezdése előtt átláthatóan, érthetően, világosan és könnyen hozzáférhetően írásban tájékoztatást kapjon az Adatkezelőtől.</p>
              </SubSection>

              <SubSection number="8.2" title="A hozzáférés joga">
                <p>Az érintett jogosult arra, hogy az Adatkezelőtől visszajelzést kapjon arra vonatkozóan, hogy személyes adatainak kezelése folyamatban van-e, és ha igen, jogosult a személyes adatokhoz és a következő információkhoz hozzáférést kapni:</p>
                <ul className="list-none space-y-1.5 mt-2 text-xs sm:text-sm">
                  {[
                    "az adatkezelés céljai",
                    "az érintett személyes adatok kategóriái",
                    "a címzettek vagy címzettek kategóriái",
                    "a személyes adatok tárolásának tervezett időtartama",
                    "helyesbítés, törlés vagy korlátozás kérelmezésének joga",
                    "a felügyeleti hatósághoz címzett panasz benyújtásának joga",
                    "az adatok forrására vonatkozó információ",
                    "az automatizált döntéshozatal ténye"
                  ].map((item, idx) => (
                    <li key={idx} className="flex gap-2 items-start">
                      <span className="text-[#D4AF37] mt-1 text-[6px]">&#9679;</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </SubSection>

              <SubSection number="8.3" title="A helyesbítéshez való jog">
                <p>Az érintett jogosult arra, hogy kérésére az Adatkezelő indokolatlan késedelem nélkül helyesbítse a rá vonatkozó pontatlan személyes adatokat.</p>
              </SubSection>

              <SubSection number="8.4" title="A törléshez való jog (elfeledtetéshez való jog)">
                <p>Az érintett jogosult arra, hogy kérésére az Adatkezelő indokolatlan késedelem nélkül törölje a rá vonatkozó személyes adatokat, ha az alábbi indokok valamelyike fennáll:</p>
                <ul className="list-none space-y-1.5 mt-2 text-xs sm:text-sm">
                  {[
                    "a személyes adatokra már nincs szükség abból a célból, amelyből azokat gyűjtötték",
                    "az érintett visszavonja a hozzájárulását, és nincs más jogalap",
                    "az érintett tiltakozik az adatkezelése ellen",
                    "a személyes adatokat jogellenesen kezelték",
                    "jogi kötelezettség teljesítéséhez törölni kell",
                    "információs társadalommal összefüggő szolgáltatások kínálásával kapcsolatosan került sor a gyűjtésre"
                  ].map((item, idx) => (
                    <li key={idx} className="flex gap-2 items-start">
                      <span className="text-[#D4AF37] mt-1 text-[6px]">&#9679;</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </SubSection>

              <SubSection number="8.5" title="Az adatkezelés korlátozásához való jog">
                <p>Az érintett jogosult arra, hogy kérésére az Adatkezelő korlátozza az adatkezelést, ha a személyes adatok pontosságát vitatja, az adatkezelés jogellenes, az Adatkezelőnek már nincs szüksége az adatokra, vagy az érintett tiltakozott az adatkezelés ellen.</p>
              </SubSection>

              <SubSection number="8.6" title="Értesítéshez való jog">
                <p>Az érintettnek joga van ahhoz, hogy kérje az Adatkezelőtől azon címzettek megadását, akikkel személyes adatait közölték. Az Adatkezelő köteles a személyes adatok helyesbítéséről, törléséről vagy adatkezelés-korlátozásáról valamennyi érintett címzettet tájékoztatni.</p>
              </SubSection>

              <SubSection number="8.7" title="Adathordozhatósághoz való jog">
                <p>Az érintett jogosult arra, hogy a rá vonatkozó személyes adatokat tagolt, széles körben használt, géppel olvasható formátumban megkapja, továbbá jogosult arra, hogy ezeket az adatokat egy másik adatkezelőnek továbbítsa.</p>
              </SubSection>

              <SubSection number="8.8" title="A tiltakozáshoz való jog">
                <p>Az érintett jogosult arra, hogy a saját helyzetével kapcsolatos okokból bármikor tiltakozzon személyes adatainak kezelése ellen. Ha a személyes adatok kezelése közvetlen üzletszerzés érdekében történik, az érintett bármikor tiltakozhat.</p>
              </SubSection>

              <SubSection number="8.9" title="Automatizált döntéshozatal, profilalkotás">
                <p>Az érintett jogosult arra, hogy ne terjedjen ki rá az olyan, kizárólag automatizált adatkezelésen – ideértve a profilalkotást is – alapuló döntés hatálya, amely rá nézve joghatással járna.</p>
              </SubSection>

              <SubSection number="8.10" title="Tájékoztatáshoz való jog adatvédelmi incidensről">
                <p>Az érintettnek joga van ahhoz, hogy tájékoztatást kapjon az Adatkezelőnél előforduló, őt érintő adatvédelmi incidensről, amennyiben az magas kockázattal jár a természetes személyek jogaira és szabadságaira nézve.</p>
              </SubSection>

              <SubSection number="8.11" title="Panasztételhez való jog">
                <p>Minden érintett jogosult arra, hogy panaszt tegyen a felügyeleti hatóságnál.</p>
                <div className="premium-card p-4 sm:p-6 mt-3">
                  <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Magyar felügyeleti hatóság</p>
                  <p className="text-white text-sm sm:text-base">Nemzeti Adatvédelmi és Információszabadság Hatóság</p>
                  <p className="text-zinc-400 text-xs sm:text-sm mt-1">1055 Budapest, Falk Miksa utca 9-11.</p>
                  <p className="text-zinc-400 text-xs sm:text-sm">Telefon: 06-1-391-1400</p>
                  <p className="text-zinc-400 text-xs sm:text-sm">
                    Email: <a href="mailto:ugyfelszolgalat@naih.hu" className="text-[#D4AF37] hover:underline">ugyfelszolgalat@naih.hu</a>
                  </p>
                  <p className="text-zinc-400 text-xs sm:text-sm">
                    Web: <a href="https://www.naih.hu" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">www.naih.hu</a>
                  </p>
                </div>
              </SubSection>

              <SubSection number="8.12" title="Bírósági jogorvoslathoz való jog">
                <p>Minden természetes és jogi személy jogosult a hatékony bírósági jogorvoslatra a felügyeleti hatóság rá vonatkozó, jogilag kötelező erejű döntésével szemben.</p>
              </SubSection>
            </Section>

            {/* Contact for rights */}
            <motion.div variants={fadeInUp} className="mt-10 sm:mt-16 premium-card p-6 sm:p-10">
              <h3 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl text-white mb-4 sm:mb-6">
                Elérhetőségek joggyakorlás érdekében
              </h3>
              <div className="space-y-3 text-zinc-400 text-sm sm:text-base">
                <p>
                  <span className="text-zinc-500 text-xs sm:text-sm">Postai levélben:</span>
                  <br />
                  <span className="text-white/80">9200 Mosonmagyaróvár, Április utca 31.</span>
                </p>
                <p>
                  <span className="text-zinc-500 text-xs sm:text-sm">E-mailben:</span>
                  <br />
                  <a href="mailto:priklermark05@gmail.com" className="text-[#D4AF37] hover:underline">priklermark05@gmail.com</a>
                </p>
                <p>
                  <span className="text-zinc-500 text-xs sm:text-sm">Személyesen:</span>
                  <br />
                  <span className="text-white/80">Telefonszámon egyeztetett módon.</span>
                </p>
                <p className="text-xs text-zinc-600 mt-4 pt-4 border-t border-white/5">
                  Telefonon személyes adatokkal kapcsolatos információkat nem áll módunkban kiadni, mivel nem tudjuk azonosítani a hívó felet.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
